import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { getScrollbarStyle } from "@ui/style/scrollbar";

interface HorizontalEdgeBlurProps {
	blurWidth: number;
	side?: "left" | "right" | "both";
	zIndex?: number;
	className?: string;
	children?: ReactNode;
}

export const HorizontalEdgeBlur: React.FC<HorizontalEdgeBlurProps> = ({
	blurWidth,
	side = "both",
	zIndex = 100,
	className,
	children,
}) => {
	const [hasScrollbar, setHasScrollbar] = useState(false);
	const [leftBlurWidth, setLeftBlurWidth] = useState(0);
	const [rightBlurWidth, setRightBlurWidth] = useState(0);
	const [blurHeight, setBlurHeight] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);

	const updateBlurDimensions = useCallback(() => {
		if (!containerRef.current) return;

		const { scrollWidth, clientWidth, clientHeight, scrollLeft } = containerRef.current;
		const hasScroll = scrollWidth > clientWidth;
		const scrollRemaining = scrollWidth - clientWidth - scrollLeft;

		setHasScrollbar(hasScroll);
		setLeftBlurWidth(Math.min(scrollLeft, blurWidth));
		setRightBlurWidth(Math.min(scrollRemaining, blurWidth));
		setBlurHeight(clientHeight);
	}, [blurWidth]);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		updateBlurDimensions();

		el.addEventListener("scroll", updateBlurDimensions);

		const mutationObserver = new MutationObserver(() => updateBlurDimensions());
		mutationObserver.observe(el, { childList: true, subtree: true });

		const resizeObserver = new ResizeObserver(() => updateBlurDimensions());
		resizeObserver.observe(el);

		return () => {
			el.removeEventListener("scroll", updateBlurDimensions);
			mutationObserver.disconnect();
			resizeObserver.disconnect();
		};
	}, [updateBlurDimensions]);

	return (
		<Container ref={containerRef} className={className}>
			{hasScrollbar && (
				<BlurContainer zIndex={zIndex}>
					{(side == "both" || side == "left") && <Blur side="left" blurWidth={leftBlurWidth} blurHeight={blurHeight} />}
					{(side == "both" || side == "right") && (
						<Blur side="right" blurWidth={rightBlurWidth} blurHeight={blurHeight} />
					)}
				</BlurContainer>
			)}
			{children}
		</Container>
	);
};

const Container = styled.div`
	${getScrollbarStyle(8)}
	position: relative;
	overflow-x: auto;
`;

const BlurContainer = styled.div<{ zIndex: number }>`
	position: sticky;
	top: 0;
	left: 0;
	right: 0;
	z-index: ${({ zIndex }) => zIndex};
`;

const Blur = styled.div<{ side: "left" | "right"; blurWidth: number; blurHeight: number }>`
	position: absolute;
	top: 0;
	width: ${({ blurWidth }) => blurWidth}px;
	height: ${({ blurHeight }) => blurHeight}px;
	pointer-events: none;

	${({ side }) =>
		side === "left"
			? `left: 0;
         background: linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0));`
			: `right: 0;
         background: linear-gradient(to left, rgba(255,255,255,1), rgba(255,255,255,0));`}
`;
